/**
 * Bar Visualizer - Classic frequency bars visualization
 */

import { Base2DVisualizer } from '../lib/base-2d-visualizer.js';
import { VisualizerRegistry } from '../visualizer.js';

// Metadata for manifest generation
export const metadata = {
  name: 'bars',
  displayName: 'Frequency Bars',
  contextType: '2d',
};

class BarsVisualizer extends Base2DVisualizer {
  static name = 'bars';
  static displayName = 'Frequency Bars';

  /**
   * Get configuration UI definition
   */
  static getConfigUI() {
    return [
      { type: 'range', id: 'barCount', label: 'Bar Count', min: 8, max: 256, default: 64 },
      { type: 'range', id: 'barGap', label: 'Bar Gap', min: 0, max: 10, default: 2, unit: 'px' },
      { type: 'range', id: 'smoothing', label: 'Smoothing', min: 0, max: 95, default: 80, scale: 0.01 },
      { type: 'color', id: 'colorStart', label: 'Color Start', default: '#00ff88' },
      { type: 'color', id: 'colorEnd', label: 'Color End', default: '#ff0088' },
      { type: 'color', id: 'backgroundColor', label: 'Background', default: '#0a0a0a' },
      { type: 'checkbox', id: 'mirror', label: 'Mirror (center)', default: true },
      { type: 'checkbox', id: 'glow', label: 'Glow effect', default: true },
      { type: 'checkbox', id: 'useLogBins', label: 'Log Frequency Bins', default: false }
    ];
  }

  /**
   * Visualizer-specific default configuration
   */
  getDefaultConfig() {
    return {
      barCount: 64,
      barGap: 2,
      minBarHeight: 2,
      mirror: true,
      useLogBins: false,
    };
  }

  /**
   * Initialize semitone smoothing buffer
   */
  init() {
    this.previousSemitones = new Float32Array(this.config.barCount);
  }

  /**
   * Returns the number of bars
   */
  getElementCount() {
    return this.config.barCount;
  }

  /**
   * Handle configuration changes
   */
  onConfigChange(changed) {
    if (changed.barCount !== undefined) {
      // Reset smoothing buffers when bar count changes
      this.previousAmplitudes = new Array(this.config.barCount).fill(0);
      this.previousSemitones = new Float32Array(this.config.barCount);
    }
    if (changed.useLogBins !== undefined) {
      // Reset semitone buffer when toggling log bins
      this.previousSemitones = new Float32Array(this.config.barCount);
    }
  }

  /**
   * Map 96 semitone bins to barCount using averaging, with smoothing
   * @param {Float32Array} semitones - 96 semitone bin values
   * @param {number} barCount - Number of bars to map to
   * @param {number} smoothing - Smoothing factor (0-1)
   * @returns {Float32Array} Mapped and smoothed values
   */
  mapSemitonesToBars(semitones, barCount, smoothing) {
    // Ensure buffer exists and is correct size
    if (!this.previousSemitones || this.previousSemitones.length !== barCount) {
      this.previousSemitones = new Float32Array(barCount);
    }

    const result = new Float32Array(barCount);
    const binsPerBar = semitones.length / barCount;

    for (let i = 0; i < barCount; i++) {
      const start = Math.floor(i * binsPerBar);
      const end = Math.floor((i + 1) * binsPerBar);
      let sum = 0;
      for (let j = start; j < end; j++) {
        sum += semitones[j];
      }
      const rawValue = sum / (end - start);

      // Apply smoothing
      result[i] = this.previousSemitones[i] * smoothing + rawValue * (1 - smoothing);
      this.previousSemitones[i] = result[i];
    }
    return result;
  }

  /**
   * Render a single frame
   */
  renderFrame(amplitudes, features) {
    const { ctx, canvas, config } = this;
    const { barCount, barGap, minBarHeight, mirror, useLogBins } = config;

    // Use semitones (log bins) if enabled and features available
    let data = amplitudes;
    if (useLogBins && features?.pitch) {
      const semitones = features.pitch.semitones();
      data = this.mapSemitonesToBars(semitones, barCount, config.smoothing);
    }

    // Calculate bar dimensions
    const totalGaps = (barCount - 1) * barGap;
    const availableWidth = canvas.width - totalGaps;
    const barWidth = availableWidth / barCount;

    // Create gradient for bars
    const gradient = ctx.createLinearGradient(0, canvas.height, 0, 0);
    gradient.addColorStop(0, config.colorStart);
    gradient.addColorStop(1, config.colorEnd);

    // Calculate vertical center for mirroring
    const centerY = canvas.height / 2;

    // Apply glow effect
    this.applyGlow(config.colorStart);

    // Draw bars
    ctx.fillStyle = gradient;

    for (let i = 0; i < barCount; i++) {
      const x = i * (barWidth + barGap);
      const normalizedHeight = data[i];
      const barHeight = Math.max(minBarHeight, normalizedHeight * (mirror ? centerY : canvas.height));

      if (mirror) {
        // Draw from center, extending up and down
        ctx.fillRect(x, centerY - barHeight, barWidth, barHeight * 2);
      } else {
        // Draw from bottom
        ctx.fillRect(x, canvas.height - barHeight, barWidth, barHeight);
      }
    }

    // Reset glow
    this.resetGlow();
  }
}

// Register with the visualizer registry
VisualizerRegistry.register(BarsVisualizer);

export { BarsVisualizer };
export default BarsVisualizer;
